import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, useCopy } from 'regen-ui'
import { toast } from 'sonner'
import { useConnection } from 'wagmi'
import Check from '~icons/lucide/check'
import Copy from '~icons/lucide/copy'
import Plus from '~icons/lucide/plus'

import * as Activity from '#/lib/activity.js'
import { checksumAddressForCopy } from '#/lib/address.js'
import * as Assets from '#/lib/assets.js'
import * as Auth from '#/lib/auth.js'
import { useDialogConnector } from '#/lib/connectors.js'
import { readOnly } from '#/lib/connectors/readOnly.js'

/**
 * Shared wallet header actions for copying the wallet address and depositing funds.
 */
export function AppHeaderActions() {
  const queryClient = useQueryClient()
  const connector = useDialogConnector()
  const connection = useConnection()
  const { chainId } = connection
  const isViewOnly = connection.connector?.type === readOnly.type
  const address = Auth.useSessionAddress() ?? connection.address

  const deposit = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      return (await provider.request({ method: 'wallet_deposit', params: [{}] })) as
        | { receipts?: readonly { logs: readonly Assets.setReceiptData.Log[] }[] }
        | undefined
    },
    onSuccess: (data) => {
      toast.success('Funds deposited')
      if (address && chainId && data?.receipts)
        for (const receipt of data.receipts)
          Assets.setReceiptData({ account: address, chainId, queryClient, receipt })
      if (address) queryClient.invalidateQueries({ queryKey: Activity.queryKey(address) })
    },
  })

  if (isViewOnly || !address) return null

  return (
    <>
      <AddressChip address={address} />
      <Button
        icon={<Plus className="size-4" />}
        loading={deposit.isPending}
        onClick={() => deposit.mutate()}
        size="medium"
        variant="primary"
      >
        Deposit
      </Button>
    </>
  )
}

function AddressChip(props: { address: string }) {
  const { address } = props
  const { copy, notifying: copied } = useCopy()

  return (
    <Button
      icon={copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      onClick={() => copy(checksumAddressForCopy(address))}
      size="medium"
      variant="secondary"
    >
      {copied ? 'Copied' : 'Copy address'}
    </Button>
  )
}
